import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  Input,
  Table,
  Select,
  Popover
} from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import { useMount } from 'react-use';
import bookingApi from '../../../../api/bookingApi';
import envConfig from '../../../../envConfig';
import {
  eventTypes,
  bookingStatus,
  getEventBookingStatus,
  formatPhoneNumber
} from '../../../utils/functions';

function SubscriptionHistory() {
  const { Option } = Select;
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [size, setSize] = useState(10);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);

  function search(queryString, tempPage) {
    if (!loading) {
      setLoading(true);
      setTotal(0);
      bookingApi
        .post('admin/subscription/payments/history', {
          searchTerm: queryString,
          size,
          page: tempPage,
          eventType: typeFilter,
          status: statusFilter
        })
        .then((response) => {
          setLoading(false);
          setTotal(response.data.data.total);
          setPayments(response.data.data.payments);
        })
        .catch((error) => {
          setLoading(false);
          console.error(error);
        });
    }
  }

  function onTableChange(tempPage, pageSize) {
    if (tempPage - 1 !== page) {
      setPage(tempPage - 1);
      search(query, tempPage - 1);
    } else {
      setSize(pageSize);
    }
  }

  useMount(() => {
    search(query, 0);
  });

  useEffect(() => {
    setTotal(0);
    setPage(0);
    search(query, 0);
  }, [size, typeFilter, statusFilter]);

  return (
    <>
      <PageHeader
        className="mb-4 px-0"
        title={t('subscription_history')}
      />
      <div className="flex space-x-2">
        <Input.Search
          className="w-full mb-1"
          key="search"
          allowClear
          loading={loading}
          enterButton={t('search')}
          onSearch={(value) => {
            setPage(0);
            setQuery(value);
            search(value, 0);
          }}
        />
        <Select
          style={{ width: '140px' }}
          value={statusFilter}
          onChange={(value) => setStatusFilter(value)}
        >
          <Option
            key="any"
            value={null}
            className="text-xs flex items-center"
          >
            {t('any')}
          </Option>
          {_.map(bookingStatus, (status) => (
            <Option
              key={status.type}
              value={status.type}
              className="text-xs flex items-center"
            >
              {t(status.name)}
            </Option>
          ))}
        </Select>
      </div>
      <Table
        pagination={{
          pageSize: size,
          showSizeChanger: true,
          defaultCurrent: 0,
          current: page + 1,
          total,
          onChange: onTableChange
        }}
        size="small"
        bordered
        rowKey="id"
        loading={loading}
        columns={[
          {
            title: t('created_on'),
            key: 'createdAt',
            dataIndex: 'createdAt',
            align: 'center',
            className: 'text-xs',
            render: (row) => (
              <div className="flex flex-col">
                <span>{moment(row).add(envConfig.REACT_APP_TIMEZONE_OFFSET, 'hours').format('ddd')}</span>
                <span>{moment(row).add(envConfig.REACT_APP_TIMEZONE_OFFSET, 'hours').format('MMM D')}</span>
                <span>{moment(row).add(envConfig.REACT_APP_TIMEZONE_OFFSET, 'hours').format('h:mm A')}</span>
              </div>
            ),
          },
          {
            title: t('status'),
            key: 'status',
            align: 'center',
            className: 'text-xs',
            render: (row) => (
              <span>{t(getEventBookingStatus(row.status))}</span>
            ),
          },
          {
            title: t('name'),
            key: 'name',
            align: 'center',
            className: 'text-xs',
            render: (row) => (
              <Popover
                content={(
                  <div>
                    <div>{`${row.firstName} ${row.lastName}`}</div>
                    <div>{row.email}</div>
                    <div>{formatPhoneNumber(row.phoneNumber)}</div>
                  </div>
                )}
              >
                <span>{`${row.firstName} ${row.lastName}`}</span>
              </Popover>
            ),
          },
          {
            title: t('membership'),
            key: 'subscriptionName',
            align: 'center',
            className: 'text-xs',
            render: (row) => (
              <span>{row.subscriptionName}</span>
            ),
          },
          {
            title: t('start_date'),
            key: 'forStartDate',
            dataIndex: 'forStartDate',
            align: 'center',
            className: 'text-xs',
            render: (row) => (
              <div className="flex flex-col">
                <span>{moment(row).add(envConfig.REACT_APP_TIMEZONE_OFFSET, 'hours').format('ddd')}</span>
                <span>{moment(row).add(envConfig.REACT_APP_TIMEZONE_OFFSET, 'hours').format('MMM D')}</span>
              </div>
            ),
          },
          {
            title: t('end_date'),
            key: 'forEndDate',
            dataIndex: 'forEndDate',
            align: 'center',
            className: 'text-xs',
            render: (row) => (
              <div className="flex flex-col">
                <span>{moment(row).add(envConfig.REACT_APP_TIMEZONE_OFFSET, 'hours').format('ddd')}</span>
                <span>{moment(row).add(envConfig.REACT_APP_TIMEZONE_OFFSET, 'hours').format('MMM D')}</span>
              </div>
            ),
          },
          {
            title: t('price'),
            key: 'amount',
            align: 'center',
            className: 'text-xs',
            render: (row) => (
              <span>${row.amount.toFixed(2)}</span>
            ),
          },
        ]}
        dataSource={payments}
      />
    </>
  );
}
export default SubscriptionHistory;
